import { Badge, Button, Card, EmptyState, Notification, Panel, Table, type TableColumn } from '@hgc/ui';
import type { JSX, ReactNode } from 'react';
import { useEffect, useMemo } from 'react';

import { usePrimaryVehicle, useVehicleState } from '../../runtime/hooks';
import { describeSetStatus, type ParameterSession } from '../params/session';
import { useOptionalSessionSnapshot } from '../params/useParameterSessionSnapshot';
import type { SetupTab } from '../tabs';
import {
  buildVehicleFacts,
  firmwareInfoOf,
  vehicleClassLabel,
  type HealthItem,
  type TodoItem,
} from './readiness';

/**
 * 机体摘要。
 *
 * 这一页回答的是"我这台飞机现在是什么状态、还差什么"。因此它的每一行都必须能追到
 * 一个真实来源（遥测读数、参数值、或"没有这个通道"），不允许出现"看起来正常"的绿勾。
 *
 * 最典型的例子是**最后一次校准时间**：界面没有校准记录，也没有从遥测判断"何时校准过"
 * 的通道，所以这里如实显示"没有记录"，并说明原因 —— 而不是显示一个像是刚校准过的日期。
 */

export interface VehicleSummaryPanelProps {
  readonly session: ParameterSession | null;
  /** 没有会话时的原因（没连机 / 链路上没有飞控） */
  readonly sessionReason: string | null;
  readonly onOpenTab: (tab: SetupTab) => void;
}

const HEADER = 'text-xs text-fg-subtle';
const VALUE = 'text-sm text-fg';

export function VehicleSummaryPanel({
  session,
  sessionReason,
  onOpenTab,
}: VehicleSummaryPanelProps): JSX.Element {
  const vehicle = usePrimaryVehicle();
  const state = useVehicleState();
  const snapshot = useOptionalSessionSnapshot(session);

  const statustext = state?.statustext;
  const firmware = useMemo(() => firmwareInfoOf(statustext ?? []), [statustext]);

  /*
   * 摘要页要用到参数总数与几个参数值，所以它也是"参数表的消费者"：
   * 在这里发起读取，用户切到参数页时表已经在了（会话会记住这一次下载，不会下两遍）。
   */
  useEffect(() => {
    void session?.ensureDownloaded();
  }, [session]);

  const facts = useMemo(() => {
    const paramValue = (name: string): number | null =>
      snapshot?.rows.find((item) => item.name === name)?.value ?? null;
    return buildVehicleFacts({
      vehicleClass: state?.vehicleClass ?? 'unknown',
      firmware,
      paramCount: snapshot?.total ?? 0,
      paramExpected: snapshot?.expected ?? null,
      downloadStatus: snapshot?.lastDownload?.status ?? null,
      missingParams: snapshot?.lastDownload?.missing.length ?? 0,
      metadataStatus: snapshot?.metadata.status ?? null,
      metadataSize: snapshot?.metadata.size ?? 0,
      frameClass: paramValue('FRAME_CLASS'),
      frameType: paramValue('FRAME_TYPE'),
      rtlAltCm: paramValue('RTL_ALT'),
      ekfHealthy: state?.ekfHealthy ?? null,
      fixType: state?.fixType ?? null,
      fixTypeName: state?.fixTypeName ?? null,
      satellites: state?.satellites ?? null,
      hdop: state?.hdop ?? null,
      voltageV: state?.voltageV ?? null,
      remainingPct: state?.remainingPct ?? null,
      vibrationClipping: state?.vibration?.clipping ?? null,
      rcChannels: state?.rcChannels ?? null,
    });
  }, [firmware, snapshot, state]);

  return (
    <div className="flex flex-col gap-3">
      <Panel
        title="机体摘要"
        description="机型、固件、参数与配置状态。每一项都写清来源；拿不到就如实说拿不到。"
      >
        <dl className="m-0 grid grid-cols-2 gap-x-6 gap-y-2 md:grid-cols-3">
          <Fact label="机型">{vehicle === null ? '未连接' : vehicleClassLabel(vehicle.vehicleClass)}</Fact>
          <Fact label="机体类型（MAV_TYPE）">{vehicle?.typeName ?? '未知'}</Fact>
          <Fact label="机体标识">{vehicle?.label ?? '未连接'}</Fact>
          <Fact label="固件版本">
            {firmware.label}
            <span className="ml-1 text-[10px] text-fg-subtle">
              {firmware.source === 'statustext'
                ? '（来自开机状态文本）'
                : '（链路上没有可用的版本通道）'}
            </span>
          </Fact>
          <Fact label="参数总数">
            {session === null
              ? '还没有飞控，读不到参数表'
              : `${snapshot?.total ?? 0} 条已读${
                  snapshot?.expected === null || snapshot?.expected === undefined
                    ? ''
                    : ` / 机体自报 ${snapshot.expected} 条`
                }`}
          </Fact>
          <Fact label="最后一次校准">
            <span className="text-caution">没有记录</span>
            <span className="ml-1 text-[10px] text-fg-subtle">（界面未存储校准记录，也不从遥测推断）</span>
          </Fact>
          <Fact label="当前模式">{state?.modeName ?? '未知'}</Fact>
          <Fact label="解锁状态">
            {state === null ? '未知' : state.armed ? '已解锁' : '未解锁'}
          </Fact>
          <Fact label="链路">{state === null ? '未连接' : state.online ? '在线' : '离线'}</Fact>
        </dl>
      </Panel>

      {session === null && sessionReason !== null ? (
        <Notification level="info" title="参数与配置状态暂时看不到">
          {sessionReason}
        </Notification>
      ) : null}

      <HealthPanel items={facts.health} />

      <TodoPanel items={facts.todo} onOpenTab={onOpenTab} />

      {snapshot === null || snapshot.failures.length === 0 ? null : (
        <Card title="参数写入未确认（需要处理）">
          <ul className="m-0 flex list-none flex-col gap-1 p-0">
            {snapshot.failures.map((failure) => (
              <li key={failure.name} className="text-xs">
                <span className="font-mono text-fg">{failure.name}</span>
                <span className="ml-1 text-critical">{describeSetStatus(failure.status)}</span>
                <span className="ml-1 text-fg-muted">
                  {failure.actual === null
                    ? '没有收到回读，结果未知'
                    : `实际回读值 ${failure.actual}`}
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-2">
            <Button size="sm" variant="secondary" onClick={() => onOpenTab('params')}>
              去参数编辑器处理
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function Fact({ label, children }: { readonly label: string; readonly children: ReactNode }): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className={HEADER}>{label}</dt>
      <dd className={`m-0 ${VALUE}`}>{children}</dd>
    </div>
  );
}

const HEALTH_COLUMNS: readonly TableColumn<HealthItem>[] = [
  { key: 'label', header: '项目', width: '12rem', render: (item) => <span className="text-xs text-fg">{item.label}</span> },
  {
    key: 'value',
    header: '读数',
    width: '14rem',
    render: (item) => <span className="text-xs tabular-nums text-fg">{item.value}</span>,
  },
  {
    key: 'level',
    header: '结论',
    width: '7rem',
    render: (item) => (
      <Badge size="sm" level={item.level} dot>
        {item.known ? (item.level === 'ok' ? '正常' : '需要关注') : '未知'}
      </Badge>
    ),
  },
  { key: 'detail', header: '说明', render: (item) => <span className="text-xs text-fg-muted">{item.detail}</span> },
];

const healthKey = (item: HealthItem): string => item.id;

function HealthPanel({ items }: { readonly items: readonly HealthItem[] }): JSX.Element {
  return (
    <Panel
      title="传感器健康"
      description="只看真实读数。没有数据的项目显示「未知」，不会当成正常。"
    >
      <Table
        aria-label="传感器健康"
        rows={items}
        columns={HEALTH_COLUMNS}
        getRowKey={healthKey}
        density="compact"
      />
    </Panel>
  );
}

function TodoPanel({
  items,
  onOpenTab,
}: {
  readonly items: readonly TodoItem[];
  readonly onOpenTab: (tab: SetupTab) => void;
}): JSX.Element {
  return (
    <Panel
      title={`还没配好的项（${items.length}）`}
      description="点每一条可以跳到对应的子页。这里的判断全部来自当前读数与参数值，不是通用检查清单。"
    >
      {items.length === 0 ? (
        <EmptyState
          level="ok"
          title="当前没有发现待配置项"
          description="这**不等于**飞机已经可以飞：可飞性还取决于现场检查（桨、电池、航区）。这一页只反映能从数据看出来的部分。"
        />
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {items.map((item) => {
            const { target, targetLabel } = item;
            return (
              <li key={item.id} className="flex items-start gap-2">
                <Badge size="sm" level={item.level} dot>
                  {item.level === 'critical'
                    ? '必须处理'
                    : item.level === 'warning'
                      ? '需要处理'
                      : '提示'}
                </Badge>
                <div className="min-w-0 flex-1">
                  <p className="m-0 text-xs font-medium text-fg">{item.label}</p>
                  <p className="m-0 text-xs text-fg-muted">{item.detail}</p>
                </div>
                {target === null || targetLabel === null ? null : (
                  <Button size="sm" variant="secondary" onClick={() => onOpenTab(target)}>
                    {targetLabel}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </Panel>
  );
}
